/**
 * Panel "Recobros" — listado de episodios de ms-ops (roadmap §F2.b).
 *
 * Sustituye la lectura diaria del Sheets de Impagos: una fila por episodio
 * (proceso × suscripción) con su estado, paso actual, nº de recibos pendientes
 * y última comunicación. La vista Impagos sigue siendo la de FACTURAS del
 * admin-api; ésta es la del MOTOR de cadencias.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { listCadences, listEpisodes, useMsOpsUnavailable } from "../../api/msops";
import { useAuth } from "../../context/AuthContext";
import {
  allCadencesSilent,
  CHANNEL_LABEL,
  EPISODE_STATUS_LABEL,
  EPISODE_STATUS_STYLES,
  EpisodeListItem,
  EpisodeStatus,
  MSOPS_MARKETS,
  MSOPS_MAX_PAGE_SIZE,
  MsOpsMarket,
  Paginated,
  PROCESS_KIND_LABEL,
  ProcessKind,
  SEND_STATUS_LABEL,
  SEND_STATUS_STYLES,
} from "../../types/msops";
import Pagination from "../OnboardingCustomers/components/Pagination";
import RecobrosTabs from "./components/RecobrosTabs";
import {
  Badge,
  COLORS,
  Card,
  describeError,
  EmptyRow,
  ErrorBanner,
  fieldStyle,
  fmtDateTime,
  inputStyle,
  labelStyle,
  Loading,
  secondaryButtonStyle,
  ShadowModeBanner,
  Td,
  Th,
  UnavailableNotice,
} from "./components/ui";

const DEFAULT_PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

const PROCESS_KINDS: ProcessKind[] = [
  "dunning",
  "renewal",
  "signature",
  "sepa_dispute",
];
const STATUSES: EpisodeStatus[] = ["open", "paused", "closed"];

export default function Recobros() {
  const { token } = useAuth();
  const unavailable = useMsOpsUnavailable();

  const [data, setData] = useState<Paginated<EpisodeListItem> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** `true` = ninguna cadencia tiene un mercado con el gate abierto. */
  const [allShadow, setAllShadow] = useState(false);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // `dunning` por defecto: la API nunca mezcla procesos sin que el caller lo
  // pida, y el panel nace para impagos. Cuando F4 meta renovaciones y firma,
  // el selector ya está aquí.
  const [processKind, setProcessKind] = useState<ProcessKind | "">("dunning");
  const [status, setStatus] = useState<EpisodeStatus | "">("");
  const [market, setMarket] = useState<MsOpsMarket | "">("");
  const [minReceipts, setMinReceipts] = useState("");
  const [disputed, setDisputed] = useState<"" | "true" | "false">("");

  // 0-based, como el componente Pagination del SPA. ms-ops es 1-based: la
  // conversión se hace justo en la llamada.
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await listEpisodes(
        {
          page: page + 1,
          pageSize,
          processKind: processKind || undefined,
          status: status || undefined,
          market: market || undefined,
          minReceipts: minReceipts ? Number(minReceipts) : undefined,
          q: debouncedSearch || undefined,
          disputed: disputed === "" ? undefined : disputed === "true",
        },
        { token },
      );
      setData(response);
    } catch (err) {
      setError(describeError(err, "No se pudieron cargar los episodios"));
    } finally {
      setLoading(false);
    }
  }, [
    token,
    page,
    pageSize,
    processKind,
    status,
    market,
    minReceipts,
    debouncedSearch,
    disputed,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Aviso global de modo shadow.
   *
   * Se carga aparte del listado y en silencio: es un dato de CONTEXTO, no de la
   * tabla. Si la llamada falla no se pinta nada y no se molesta al operador con
   * un segundo banner de error — el aviso es útil cuando se puede afirmar con
   * certeza, y ante la duda callar es mejor que decir "no envía" sin saberlo.
   */
  useEffect(() => {
    let alive = true;
    listCadences({ page: 1, pageSize: MSOPS_MAX_PAGE_SIZE }, { token })
      .then((res) => {
        if (alive) setAllShadow(allCadencesSilent(res.items));
      })
      .catch(() => {
        if (alive) setAllShadow(false);
      });
    return () => {
      alive = false;
    };
  }, [token]);

  const clearFilters = () => {
    setSearch("");
    setProcessKind("dunning");
    setStatus("");
    setMarket("");
    setMinReceipts("");
    setDisputed("");
    setPage(0);
  };

  // Cualquier cambio de filtro vuelve a la primera página: mantener el offset
  // con otro conjunto de resultados enseña una página vacía sin explicación.
  const onFilterChange = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value);
    setPage(0);
  };

  const rows = useMemo(() => data?.items ?? [], [data]);

  // El passthrough respondió 404: ms-ops no existe en este entorno. Se cambia
  // la pantalla entera por el aviso en vez de enseñar filtros que no sirven.
  if (unavailable) {
    return <UnavailableNotice />;
  }

  if (loading && !data) {
    return <Loading label="Cargando episodios…" />;
  }

  return (
    <div style={{ padding: "1rem 1.5rem", maxWidth: 1600, margin: "0 auto" }}>
      <RecobrosTabs />
      <div
        style={{
          marginBottom: "0.75rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "1rem",
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: "1.15rem", color: COLORS.heading }}>
            Casos de recobro
          </h2>
          <p
            style={{
              margin: "0.25rem 0 0",
              color: COLORS.muted,
              fontSize: ".85rem",
            }}
          >
            Cada caso de impago abierto por el motor: estado, paso actual y
            última comunicación. Para trabajar la cola del día usa «Mis
            tareas»; para ver el dinero, «Deuda por cliente».
          </p>
        </div>
        
      </div>

      {allShadow && <ShadowModeBanner />}

      <Card style={{ padding: "0.75rem 0.95rem", marginBottom: "0.65rem" }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.75rem",
            alignItems: "flex-end",
          }}
        >
          <label style={{ ...fieldStyle, flex: "1 1 220px" }}>
            <span style={labelStyle}>Buscar (email o lead)</span>
            <input
              type="search"
              value={search}
              placeholder="cliente@ejemplo.com"
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              style={inputStyle}
            />
          </label>

          <label style={fieldStyle}>
            <span style={labelStyle}>Proceso</span>
            <select
              value={processKind}
              onChange={(e) =>
                onFilterChange(setProcessKind)(
                  e.target.value as ProcessKind | "",
                )
              }
              style={inputStyle}
            >
              <option value="">Todos</option>
              {PROCESS_KINDS.map((k) => (
                <option key={k} value={k}>
                  {PROCESS_KIND_LABEL[k]}
                </option>
              ))}
            </select>
          </label>

          <label style={fieldStyle}>
            <span style={labelStyle}>Estado</span>
            <select
              value={status}
              onChange={(e) =>
                onFilterChange(setStatus)(e.target.value as EpisodeStatus | "")
              }
              style={inputStyle}
            >
              <option value="">Todos</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {EPISODE_STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </label>

          <label style={fieldStyle}>
            <span style={labelStyle}>Mercado</span>
            <select
              value={market}
              onChange={(e) =>
                onFilterChange(setMarket)(e.target.value as MsOpsMarket | "")
              }
              style={inputStyle}
            >
              <option value="">Todos</option>
              {MSOPS_MARKETS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </label>

          <label style={fieldStyle}>
            <span style={labelStyle}>Recibos ≥</span>
            <input
              type="number"
              min={0}
              value={minReceipts}
              onChange={(e) => onFilterChange(setMinReceipts)(e.target.value)}
              style={{ ...inputStyle, width: "5rem" }}
            />
          </label>

          <label style={fieldStyle}>
            <span style={labelStyle}>Disputa</span>
            <select
              value={disputed}
              onChange={(e) =>
                onFilterChange(setDisputed)(
                  e.target.value as "" | "true" | "false",
                )
              }
              style={inputStyle}
            >
              <option value="">Todos</option>
              <option value="true">Sólo disputados</option>
              <option value="false">Sin disputa</option>
            </select>
          </label>

          <button
            type="button"
            onClick={clearFilters}
            style={secondaryButtonStyle}
          >
            Limpiar
          </button>
        </div>
      </Card>

      {error && <ErrorBanner message={error} />}

      <Card
        style={{
          opacity: loading ? 0.6 : 1,
          transition: "opacity .15s ease",
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: ".85rem",
            }}
          >
            <thead>
              <tr style={{ background: COLORS.subtle }}>
                <Th>Cliente / Lead</Th>
                <Th>Mercado</Th>
                <Th>Canal</Th>
                <Th style={{ textAlign: "right" }}>Recibos</Th>
                <Th>Estado</Th>
                <Th>Paso actual</Th>
                <Th>Última comunicación</Th>
                <Th style={{ textAlign: "right" }}>Notas</Th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <EmptyRow colSpan={8}>
                  Sin episodios para los filtros aplicados
                </EmptyRow>
              ) : (
                rows.map((row) => <Row key={row.id} row={row} />)
              )}
            </tbody>
          </table>
        </div>

        {data && data.total > 0 && (
          <Pagination
            page={page}
            pageSize={pageSize}
            total={data.total}
            onPageChange={(p) => setPage(p)}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPage(0);
            }}
            disabled={loading}
          />
        )}
      </Card>
    </div>
  );
}

function Row({ row }: { row: EpisodeListItem }) {
  const statusStyle = EPISODE_STATUS_STYLES[row.status] ?? {
    bg: "#e9ecef",
    fg: "#495057",
  };
  const last = row.lastCommunication;

  return (
    <tr style={{ borderBottom: `1px solid ${COLORS.rowBorder}` }}>
      <Td>
        <Link
          to={`/operations/recobros/${row.id}`}
          style={{
            color: COLORS.link,
            textDecoration: "none",
            fontWeight: 600,
          }}
        >
          {row.subscriptionRef}
        </Link>
        <div style={{ fontSize: ".7rem", color: COLORS.muted }}>
          {row.leadUlid ?? "sin lead"} · {PROCESS_KIND_LABEL[row.processKind]}
        </div>
      </Td>
      <Td mono>{row.market}</Td>
      <Td>
        {row.brokerId ? (
          <Badge bg="#e7f1ff" fg="#0069d9" title={`Broker ${row.brokerId}`}>
            Broker
          </Badge>
        ) : (
          <Badge bg="#f8f9fb" fg="#73879C">
            D2C
          </Badge>
        )}
      </Td>
      <Td
        mono
        style={{
          textAlign: "right",
          fontWeight: 700,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {row.pendingInvoicesCount}
      </Td>
      <Td>
        <div
          style={{ display: "flex", gap: "0.3rem", flexWrap: "wrap" }}
        >
          <Badge bg={statusStyle.bg} fg={statusStyle.fg}>
            {EPISODE_STATUS_LABEL[row.status]}
          </Badge>
          {row.disputed && (
            <Badge
              bg="#fff3cd"
              fg="#856404"
              title="La cadencia queda congelada mientras dure la disputa"
            >
              Disputa
            </Badge>
          )}
        </div>
      </Td>
      <Td mono style={{ fontSize: ".78rem" }}>
        {row.currentStep ?? "—"}
      </Td>
      <Td>
        {last ? (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: ".35rem" }}>
              <span>{CHANNEL_LABEL[last.channel] ?? last.channel}</span>
              <Badge
                bg={SEND_STATUS_STYLES[last.status]?.bg ?? "#e9ecef"}
                fg={SEND_STATUS_STYLES[last.status]?.fg ?? "#495057"}
              >
                {SEND_STATUS_LABEL[last.status] ?? last.status}
              </Badge>
            </div>
            <div style={{ fontSize: ".7rem", color: COLORS.muted }}>
              {fmtDateTime(last.sentAt ?? last.dueAt)}
            </div>
          </>
        ) : (
          "—"
        )}
      </Td>
      <Td mono style={{ textAlign: "right" }}>
        {row.notesCount > 0 ? row.notesCount : "—"}
      </Td>
    </tr>
  );
}
